import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ISchool } from 'Src/ng2/shared/typings/interfaces/school.interface';
import { UrlNavigationService } from 'Src/ng2/shared/services/url-navigation/url-navigation.service';
import { ENTITY_TYPE } from 'Src/ng2/shared/typings/types/entity-type.types';
import { DeferrableLoad, isLoadDeferred } from '../../deferred-route-loads';
import { StoreDataService } from 'Src/ng2/shared/services/store-data-service/store-data.service';

@Injectable({ providedIn: 'root' })
/**
 * Holds a child activation until the school document is in the store, unless that child opted out
 * with `data.defer: [DeferrableLoad.School]`.
 *
 * On canActivateChild rather than canActivate because the entity route is reused on an in-entity
 * navigation — its canActivate would not re-run, so `defer` would leak from the first page entered
 * to every page after it.
 */
export class SchoolReadyGuard implements CanActivateChild {
  constructor (
    private router: Router,
    private storeData: StoreDataService,
  ) {}

  canActivateChild (routeSnapshot: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<UrlTree | boolean> {
    const entityType = UrlNavigationService.getEntityType(routeSnapshot);
    const entityId = UrlNavigationService.getEntityId(routeSnapshot);
    // The legacy `:schoolId` tree names a school while carrying no entityType, so an absent type
    // admits rather than excludes.
    const isSingleSchool = !!entityId && (!entityType || entityType === ENTITY_TYPE.SCHOOL);

    const deferred = isLoadDeferred(state, DeferrableLoad.School);

    if (!isSingleSchool) return of(true);
    if (deferred) return of(true);

    return this.storeData.awaitLoaded$<ISchool>(DeferrableLoad.School, entityId).pipe(
      map((school: ISchool | null) => (!!school || this.router.createUrlTree(['/not-found']))),
    );
  }
}
